import { Formik } from 'formik'
import React, { useContext } from 'react'
import { Button } from '../../../components/Button'
import {
  ButtonColumn,
  ButtonRow,
  FormSubGroupsList,
  FormSubGroupWrapper,
  Section,
  SectionColumn,
  FormSubGroup,
  FormSubGroupHeader,
  FormSubGroupHeading,
} from '../../../components/layout'
import {
  BusinessIdInput,
  TextInput,
  SelectInput,
  TextAreaInput,
  PhoneInput,
  EmailInput,
} from '../../../components/layout/form/input'
import { FormikForm as Form } from '../../../components/layout/form/style'
import { CompanyCustomerFormValues, EditCompanyResponse } from '../types'
import { EditCompanyCustomerResponse } from './types'
import { ScrollToError } from '../../../components/layout/form/ScrollToError'
import { deliveryMethodCompanyOptions } from '../options'
import { formatMessageSafe, translateInputOptions } from '../../../util/translateInputOptions'
import { DeliveryMethod } from '../../../common/types'
import { eInvoiceOperatorOptions } from './eInvoiceOperatorOptions'
import { StateStore } from '../../StoreProvider'
import { acceptedRoles } from '../../TowingOrderPage/AddTowingOrder/TowingOrderSettingsForm'
import { customerNotificationOptions } from '../../../common/options'
import { useIntl } from 'react-intl'
import { useSettings } from '../../Parameters'
import { messages } from '../messages'

interface FormProps {
  editCompanyCustomer: (values: CompanyCustomerFormValues) => any
  onCloseForm: (t: EditCompanyResponse) => void
  onCancelForm: () => void
  initialValues: CompanyCustomerFormValues
  loading: boolean
}

const EditCompanyCustomerFormIntl: React.FunctionComponent<FormProps> = ({
  onCancelForm,
  onCloseForm,
  editCompanyCustomer,
  initialValues,
  loading,
}) => {
  const intl = useIntl();
  const formatMessage = formatMessageSafe(intl)
  const translatedDeliveryMethodOptions = translateInputOptions(deliveryMethodCompanyOptions, formatMessage)
  const settings = useSettings()
  const {
    state: { currentUser },
  } = useContext(StateStore)

  const handleSubmit = async (values: CompanyCustomerFormValues) => {
    try {
      const fullResponse = await editCompanyCustomer(values)
      const response: EditCompanyCustomerResponse = fullResponse.data
      if (response.editCompanyCustomer.__typename === 'EditCompanyCustomerSuccess') {
        onCloseForm(response.editCompanyCustomer)
      } else {
        console.log('Error saving: ', response.editCompanyCustomer.__typename)
      }
    } catch (err) {
      console.log('Error updating customer', err)
    }
  }
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values: CompanyCustomerFormValues) => {
        handleSubmit(values)
      }}
    >
      {({ values, submitForm }) => {
        console.log('EditCompanyCustomerFormIntl', values)
        return (
          <Form>
            <FormSubGroupsList>
              <FormSubGroupWrapper>
                <FormSubGroupHeader>
                  <FormSubGroupHeading>
                    {formatMessage(messages.customerRegistryHeading, { name: values.companyName })}
                  </FormSubGroupHeading>
                </FormSubGroupHeader>

                <FormSubGroup>
                  <Section>
                    <SectionColumn>
                      <TextInput label={formatMessage(messages.identifier)} name="identifier" />
                    </SectionColumn>
                    <SectionColumn>
                      <BusinessIdInput label={formatMessage(messages.businessId)} name="businessId" />
                    </SectionColumn>
                    <SectionColumn>
                      <TextInput label={formatMessage(messages.company)} name="companyName" />
                    </SectionColumn>
                    <SectionColumn>
                      <TextInput label="c/o" name="co" />
                    </SectionColumn>
                    <SectionColumn>
                      <TextInput label={formatMessage(messages.address)} name="address" />
                    </SectionColumn>
                    <SectionColumn>
                      <TextInput label={formatMessage(messages.zipcode)} name="zipcode" />
                    </SectionColumn>
                    <SectionColumn>
                      <TextInput label={formatMessage(messages.city)} name="city" />
                    </SectionColumn>
                    <SectionColumn>
                      <TextInput label={formatMessage(messages.emailAddress)} name="email" />
                    </SectionColumn>
                    <SectionColumn>
                      <TextInput label={formatMessage(messages.contactPerson)} name="contactPerson" />
                    </SectionColumn>
                    <SectionColumn>
                      <PhoneInput label={formatMessage(messages.contactPersonPhone)} name="phone" />
                    </SectionColumn>
                    <SectionColumn>
                      <EmailInput label={formatMessage(messages.contactPersonEmail)} name="contactPersonEmail" />
                    </SectionColumn>
                    <SectionColumn>
                      <TextInput label={formatMessage(messages.eInvoiceAddress)} name="eInvoiceAddress" />
                    </SectionColumn>
                    <SectionColumn>
                      <SelectInput
                        label={formatMessage(messages.eInvoiceOperator)}
                        name="eInvoiceOperator"
                        disabled={Boolean(values.deliveryMethod !== DeliveryMethod.e_invoice)}
                        options={eInvoiceOperatorOptions}
                      />
                    </SectionColumn>
                    <SectionColumn>
                      <SelectInput
                        label={formatMessage(messages.deliveryMethodLabel)}
                        name="deliveryMethod"
                        options={translatedDeliveryMethodOptions}
                        searchable={false}
                      />
                    </SectionColumn>
                    <SectionColumn>
                      <TextInput label={formatMessage(messages.extraInfo)} name="extraInfo" />
                    </SectionColumn>
                    {settings &&
                      settings.notifications.enabled &&
                      currentUser &&
                      acceptedRoles.includes(currentUser.role) && (
                        <SectionColumn>
                          <SelectInput
                            label={formatMessage(messages.notificationMethod)}
                            name="notificationMethod"
                            options={translateInputOptions(customerNotificationOptions, formatMessage)}
                            searchable={false}
                          />
                        </SectionColumn>
                      )}
                  </Section>
                  <Section>
                    <SectionColumn>
                      <TextAreaInput label={formatMessage(messages.notes)} name="additionalInfo" />
                    </SectionColumn>
                  </Section>
                  <ButtonRow>
                    <ButtonColumn>
                      <Button category="save" label={formatMessage(messages.save)} onClick={() => submitForm()} loading={loading} />
                    </ButtonColumn>
                    <ButtonColumn>
                      <Button category="cancelSecondary" label={formatMessage(messages.close)} onClick={onCancelForm} />
                    </ButtonColumn>
                  </ButtonRow>
                </FormSubGroup>
              </FormSubGroupWrapper>
            </FormSubGroupsList>
            <ScrollToError />
          </Form>
        )
      }}
    </Formik>
  )
}

export const EditCompanyCustomerForm = EditCompanyCustomerFormIntl
